/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */
/* eslint-disable max-statements */
/* eslint-disable no-await-in-loop */

import AssertionError from 'assertion-error';

import ErrorName from '../../error/error-name';
import PromiseUtil from '../../promise/promise-util';
import promisifyTimeWatcher from '../../promisify/promisify-time-watcher';
import ScheduleContent from '../../schedule/schedule-content';
import ViewerDelayerFactory from '../../viewer/delayer/viewer-delayer-factory';
import { BaseContentController } from './base-content-controller';
import { createContentController } from './content-controller-util';
import { generatePlayErrorElem } from './content-error-overlay';
import {
  DeterministicPlaylistSimulator,
  SimulatorUnrecoverableError
} from './playlist-simulator';

/**
 * @import { ContentController, CreateContentControllerDeps, Spot, SpotContent } from '.'
 * @import { Content } from '../content'
 * @import { Logger } from '../../logger/logger'
 * @import { PreloadStrategy } from '../../preload-strategy'
 * @import UnixTimestamp from '../../unix-timestamp/unix-timestamp'
 * @import { IVariablesNamespaceRegistry } from '../../variables'
 * @import ContentControllerFactory from './content-controller-factory'
 * @import { ContentInfo } from '../info/content-info'
 */

/**
 * @typedef {{spot: Spot<SpotContent | null>; contentController: ContentController}} DeterministicContent
 */

/**
 * @implements {ContentController}
 */
export class PlaylistDeterministicContentController extends BaseContentController {
  /**
   * @param {ContentControllerFactory} contentControllerFactory
   * @param {ContentInfo} contentInfo
   * @param {Content} content
   * @param {Logger} logger
   * @param {PreloadStrategy} preloadStrategy
   * @param {HTMLElement} parentEl
   * @param {number} parentVolume
   * @param {UnixTimestamp} unixTimestamp
   * @param {IVariablesNamespaceRegistry} variablesNamespaceRegistry
   */
  constructor(
    contentControllerFactory,
    contentInfo,
    content,
    logger,
    preloadStrategy,
    parentEl,
    parentVolume,
    unixTimestamp,
    variablesNamespaceRegistry
  ) {
    const playlist = content.playlist;
    if (!playlist || !playlist.deterministic) {
      throw new Error('content should be deterministic playlist');
    }

    super();
    this.logger = logger;
    this.preloadStrategy = preloadStrategy;
    this.parentEl = parentEl;
    this.parentVolume = parentVolume;
    this.unixTimestamp = unixTimestamp;
    this.variablesNamespaceRegistry = variablesNamespaceRegistry;

    /** @type {AbortController | undefined} */
    this.abortController = undefined;
    /** @type {DeterministicContent | undefined} */
    this.currentDeterministicContent = undefined;
    this.delayer = new ViewerDelayerFactory({ delay: 1000, limit: 5 }).create();
    /** @type {CreateContentControllerDeps} */
    this.createContentControllerDeps = {
      contentControllerFactory,
      contentInfo,
      el: parentEl
    };
    this.loopQueue = Promise.resolve();
    /** @type {DeterministicContent | undefined} */
    this.nextDeterministicContent = undefined;
    this.playlist = playlist;
    /** @type {Spot<SpotContent | null> | undefined} */
    this.prevSpot = undefined;
    /** @type {(() => void) | undefined} */
    this.removePictogram = undefined;
    this.simulator = this.createSimulator();
    this.unixTimeOffset = unixTimestamp.offset();

    this.now = this.now.bind(this);
  }

  /**
   * @returns {DeterministicPlaylistSimulator}
   */
  createSimulator() {
    return new DeterministicPlaylistSimulator(
      this.playlist,
      this.parentVolume,
      this.unixTimestamp,
      this.variablesNamespaceRegistry
    );
  }

  /** @type {ContentController['preload']} */
  async preload() {
    await super.preloadCallback(async () => {
      // TODO: V době preloadu nevíme, co by se mělo přednačíst. Možná předávat
      // preload offset (délka aktuálně přehrávaného media), a toto medium přednačíst.
      this.abortController = await new AbortController();
      this.loopQueue = Promise.resolve();
    });
  }

  /** @type {ContentController['restart']} */
  async restart() {
    // Není potřeba dělat nic, protože reset deterministického playlistu způsobí spuštění ve stejném místě,
    // kde playlist skončil.
  }

  /** @type {ContentController['start']} */
  async start() {
    const { abortController } = this;
    if (!abortController) {
      throw new AssertionError('AbortController not set');
    }

    // eslint-disable-next-line require-await
    await super.startCallback(async () => {
      // Spuštění asynchronně přehrávací smyčky.
      (async () => {
        while (!abortController.signal.aborted) {
          try {
            this.loopQueue = this.startDeterministic(abortController).then(
              (success) =>
                success ? this.delayer.succeed() : this.delayer.fail()
            );
            await this.loopQueue;
          } catch (cause) {
            if (cause instanceof SimulatorUnrecoverableError) {
              this.logger.error(cause);
              return;
            }
            this.logger.error(
              new Error('PlaylistDeterministicContentController#start()', {
                cause
              })
            );
            await this.delayer.fail();
            this.loopQueue = Promise.resolve();
          }
        }
      })();
    });
  }

  /**
   * @param {AbortController} abortController
   * @returns {Promise<boolean>}
   */
  async startDeterministic(abortController) {
    this.unixTimeOffset = this.unixTimestamp.offset();
    // Uložení příznaku jestli je deterministický obsah preloadovaný z předchozího volání.
    const hasPreloadedDeterministicContent = Boolean(
      this.nextDeterministicContent
    );
    /** @type {number | undefined} */
    let time = this.now();

    if (this.nextDeterministicContent) {
      this.currentDeterministicContent = this.nextDeterministicContent;
      this.nextDeterministicContent = undefined;
    } else {
      /**  @type {Spot<SpotContent | null> | undefined} */
      let spot;
      try {
        spot = await this.simulator.next();
      } catch (cause) {
        throw new SimulatorUnrecoverableError(
          'DeterministicPlaylistSimulator#next()',
          { cause }
        );
      }

      if (!spot || !spot.content) {
        return false;
      }
      const contentController = createContentController(
        spot,
        this.createContentControllerDeps
      );
      if (!contentController) {
        return false;
      }
      this.currentDeterministicContent = { contentController, spot };
    }

    const { contentController, spot } = this.currentDeterministicContent;
    if (!spot || !spot.content || !spot.content.model) {
      return false;
    }
    const contentModel = spot.content.model;

    const durationUs = spot.time + spot.duration - time;
    const durationMs = durationUs / 1000;

    let played = false;
    const onError = () => (played = false);
    /** @type {(content: Content) => void} */
    const onPlayed = (content) => {
      if (ScheduleContent.isLeafContent(spot.content?.model)) {
        return;
      }
      // Emitování played eventů pouze pro nekoncové obsahy.
      this.emitter.emit('played', content);
    };
    /** @type {(content: Content) => void} */
    const onPlaying = (content) => {
      if (ScheduleContent.isLeafContent(spot.content?.model)) {
        return;
      }
      // Emitování playing eventů pouze pro nekoncové obsahy.
      this.emitter.emit('playing', content);
    };

    contentController.on('error', onError);
    contentController.on('played', onPlayed);
    contentController.on('playing', onPlaying);

    const abortSignal = abortController.signal;
    const timeWatcher = promisifyTimeWatcher(
      spot.time + spot.duration,
      this.now,
      { abortSignal }
      // Umyslné ignorování chyby. Protože nás nezajímá informace o přerušení.
    ).catch(() => {});
    try {
      // Kontrola jestli je doba přehrávání menší než zadaný interval.
      // Toto se kontroluje kvůli tomu, že na tak krátkou chvílí nemá cenu pouštět přehrávání.
      // TODO: jaký časový interval
      if (durationMs > 1000) {
        if (!hasPreloadedDeterministicContent) {
          await contentController.preload();
        }

        time = this.now();
        await contentController.start((time - spot.time) / 1000);
        this.emitter.emit('playing', contentModel);
        played = true;
      }
    } catch (err) {
      const errorEl = generatePlayErrorElem(
        /** @type {Error} */ (err),
        contentModel
      );
      this.parentEl.appendChild(errorEl);
      this.removePictogram = () => {
        this.parentEl.removeChild(errorEl);
        this.removePictogram = undefined;
      };
    }

    // Přednačtení dalšího media v pořadí.
    await this.simulator
      .peek(spot)
      .then(async (nextSpot) => {
        const prev = this.prevSpot?.content?.model;
        const curr = contentModel;
        const next = nextSpot?.content?.model;
        if (
          !(next && prev && this.preloadStrategy.canPreload(next, curr, prev))
        ) {
          return;
        }

        const nextContentController = createContentController(
          nextSpot,
          this.createContentControllerDeps
        );

        // Délka zpoždění byla zjištěná experimentálně na TV. Zpoždění jen a pouze kvůli webOS Signage 4.0 a 4.1,
        // kde docházelo k černání videí po kterých následoval obrázek. Zvuk videí na pozadí hrál, fungovalo onended.
        // Jediné, co dokázalo zčernání videa opravit bylo kliknutí na libovolný element v DOM přes DevTools.
        // Zpoždění teoreticky stačí 100 ms, ale po vzoru zaniklého ViewerThred, necháme 200 ms.
        await PromiseUtil.wait(200);
        await nextContentController.preload();
        // Nastavuje se až po úspěšném volání preload.
        this.nextDeterministicContent = {
          spot: nextSpot,
          contentController: nextContentController
        };
      })

      // Preload se nepovedl, např. kvůli vyčerpání aktuálně vypůjčených zdrojů.
      .catch(() => {});

    await timeWatcher
      .catch((/** @type {Error} */ error) => {
        // Jiné, než AbortError, nejsou očekavané.
        if (error.name !== ErrorName.AbortError) {
          throw error;
        }
      })
      .finally(async () => {
        if (played) {
          this.emitter.emit('played', contentModel);
        }

        await contentController.stop().catch(() => {});
        this.prevSpot = spot;

        this.removePictogram?.();
        contentController.off('error', onError);
        contentController.off('played', onPlayed);
        contentController.off('playing', onPlaying);
        // Umyslené ignorování chyby, které mohly vzniknout při zastavování.
        this.currentDeterministicContent = undefined;
      });
    return true;
  }

  /** @type {ContentController['stop']} */
  async stop() {
    try {
      await super.stopCallback(async () => {
        this.abortController?.abort();
        // Prvně se musí počkat na dokončení přehrávací smyčky.
        await this.loopQueue.catch(() => {});

        this.delayer.destroy();
        await this.currentDeterministicContent?.contentController
          .stop()
          .catch(() => {});
        await this.nextDeterministicContent?.contentController
          .stop()
          .catch(() => {});

        this.currentDeterministicContent = undefined;
        this.nextDeterministicContent = undefined;
        this.prevSpot = undefined;
        this.loopQueue = Promise.resolve();
      });
    } catch {
      // Úmyslené ignorování chyby. Pokud chyba vznikla při stop,
      // tak aby metoda stop byla přerušena. Ten případ by neměl nastávat.
    }
  }

  /**
   * Zjistí aktuální čas.
   * @private
   * @returns {number} Aktuální čas v mikrosekundách.
   */
  now() {
    return (this.unixTimestamp.now() + this.unixTimeOffset) * 1000;
  }
}
